import { SetStateAction, Dispatch, useState } from 'react';
import generalClasses from '../tabs/styles.module.scss';
import { IInfrastructureProjectObject, IStreet } from '../../../types/project.types';
import ReactSelect from 'react-select';
import { findOptionByValue, isEmpty, t } from '../../../helpers/utils';
import Field from '../../../components/field/field.component';
import { PlusIcon } from '../../../assets/icons';

interface IProps {
    data: IInfrastructureProjectObject;
    setData: Dispatch<SetStateAction<IInfrastructureProjectObject>>;
    lang: 'ru' | 'kz';
    newFund: { labelRu: string; labelKz: string; value: string } | null;
    setNewFund: Dispatch<React.SetStateAction<Record<number, { labelRu: string; labelKz: string; value: string }>>>,
    isAddingFund: boolean;
    setIsAddingFund: Dispatch<SetStateAction<boolean>>;
    parentErrors: Record<string, boolean>;
    street: IStreet;
    setStreet?: Dispatch<SetStateAction<IStreet | null>>;
}

const translationKey = 'projects.infrastructure-projects';

const StreetDocumentation = ({
    data,
    setData,
    lang,
    newFund,
    setNewFund,
    isAddingFund,
    setIsAddingFund,
    parentErrors,
    street,
    setStreet
}: IProps) => {
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const suffix = lang === 'ru' ? 'Ru' : 'Kz';

    const documentationStatusOptions = [
        { label: t(`${translationKey}.HAVE`, lang), value: 'HAVE' },
        { label: t(`${translationKey}.IN_PROCESS`, lang), value: 'IN_PROCESS' },
        { label: t(`${translationKey}.NOT_HAVE`, lang), value: 'NOT_HAVE' },
    ];

    const handleInputChange = (field: keyof IStreet, value: any) => {
        if (setStreet) {
            setStreet(prev => ({ ...(prev as IStreet), [field]: value }));
        } else {
            setData(prev => ({
                ...prev,
                infrastructureEntity: {
                    ...prev.infrastructureEntity,
                    streetList: prev.infrastructureEntity.streetList?.map(s =>
                        s.id === street.id ? { ...s, [field]: value } : s
                    )
                }
            }));
        }
    };

    const handleFundChange = (
        key: string,
        field: 'labelRu' | 'labelKz' | 'value',
        value: string
    ) => {
        if (!street) return;

        if (setStreet) {
            setStreet(prev => ({
                ...(prev as IStreet),
                requiredFund: {
                    ...(prev?.requiredFund || {}),
                    [key]: {
                        ...(prev?.requiredFund?.[key] || {
                            labelRu: '',
                            labelKz: '',
                            value: ''
                        }),
                        [field]: value
                    }
                }
            }));
        } else {
            setData(prev => {
                const current = prev.infrastructureEntity.streetList?.find(s => s.id === street.id);
                const existing = current?.requiredFund?.[key] ?? {
                    labelRu: '',
                    labelKz: '',
                    value: ''
                };

                return {
                    ...prev,
                    infrastructureEntity: {
                        ...prev.infrastructureEntity,
                        streetList: prev.infrastructureEntity.streetList?.map(s =>
                            s.id === street.id
                                ? {
                                    ...s,
                                    requiredFund: {
                                        ...s.requiredFund,
                                        [key]: { ...existing, [field]: value }
                                    }
                                }
                                : s
                        )
                    }
                };
            });
        }
    };

    const startAddFund = () => {
        if (!street) return;
        setNewFund(prev => ({ ...prev, [street.id]: { labelRu: '', labelKz: '', value: '' } }));
        setIsAddingFund(true);
    };

    const cancelAddFund = () => {
        if (!street) return;
        setIsAddingFund(false);
        setNewFund(prev => ({ ...prev, [street.id]: { labelRu: '', labelKz: '', value: '' } }));
    };

    const saveNewFund = () => {
        if (!newFund || !street) return;

        const newErrors: Record<string, boolean> = {};
        ['labelRu', 'labelKz', 'value'].forEach(field => {
            if (isEmpty(newFund[field as keyof typeof newFund])) {
                newErrors[field] = true;
            }
        });

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        if (setStreet) {
            setStreet(prev => ({
                ...(prev as IStreet),
                requiredFund: {
                    ...(prev?.requiredFund || {}),
                    [`created-${Date.now()}`]: { ...newFund }
                }
            }));
        } else {
            setData(prev => ({
                ...prev,
                infrastructureEntity: {
                    ...prev.infrastructureEntity,
                    streetList: prev.infrastructureEntity.streetList?.map(s =>
                        s.id === street.id
                            ? {
                                ...s,
                                requiredFund: {
                                    ...s.requiredFund,
                                    [`created-${Date.now()}`]: { ...newFund }
                                }
                            }
                            : s
                    )
                }
            }));
        }

        cancelAddFund();
    };

    if (!street) return null;

    return (
        <div className={`${generalClasses['field-block']} ${generalClasses['field-block--gray']}`}>
            <div className={`${generalClasses['field-block__label']} ${generalClasses['required']}`}>
                {t(`${translationKey}.documentation-status`, lang)}
            </div>

            <ReactSelect
                options={documentationStatusOptions}
                value={findOptionByValue(street.documentationStatus, documentationStatusOptions)}
                onChange={(option) => handleInputChange('documentationStatus', option?.value || '')}
                placeholder=""
                className={generalClasses['react-select']}
            />

            {street.documentationStatus === 'HAVE' && (
                <>
                    <Field
                        label={t(`${translationKey}.workStart`, lang)}
                        value={street.workStart || ''}
                        onChange={(val) => handleInputChange('workStart', val)}
                        required
                        type="date"
                        error={parentErrors.workStart}
                    />
                    <Field
                        label={t(`${translationKey}.workEnd`, lang)}
                        value={street.workEnd || ''}
                        onChange={(val) => handleInputChange('workEnd', val)}
                        required
                        type="date"
                        error={parentErrors.workEnd}
                    />
                    <Field
                        label={t(`${translationKey}.requiredFundSource`, lang)}
                        value={street[`requiredFundSource${suffix}`] || ''}
                        onChange={(val) => handleInputChange(`requiredFundSource${suffix}`, val)}
                        required
                        type="text"
                        error={parentErrors[`requiredFundSource${suffix}`]}
                    />
                    <Field
                        label={t(`${translationKey}.requiredFundAmount`, lang)}
                        value={street.requiredFundAmount || ''}
                        onChange={(val) => handleInputChange('requiredFundAmount', val)}
                        required
                        type="number"
                        error={parentErrors.requiredFundAmount}
                    />

                    <div className={generalClasses['field-block__title']}>
                        {t(`${translationKey}.fundSources`, lang)}
                    </div>

                    {street.requiredFund &&
                        Object.entries(street.requiredFund).map(([key, value]) => (
                            <Field
                                label={value[`label${suffix}`] || ''}
                                value={value.value}
                                onChange={(val) => handleFundChange(key, 'value', val.toString())}
                                required
                                type="number"
                                key={key}
                                error={parentErrors[`requiredFund.${key}-value`]}
                            />
                        ))}

                    {!isAddingFund && (
                        <div className={generalClasses['field-block__actions']}>
                            <button onClick={startAddFund}>
                                <PlusIcon /> {t(`actions.add`, lang)}
                            </button>
                        </div>
                    )}

                    {isAddingFund && newFund && (
                        <div className={generalClasses['field-block']}>
                            <Field
                                label={t(`${translationKey}.label`, lang)}
                                value={newFund[`label${suffix}`] || ''}
                                onChange={(val) =>
                                    setNewFund(prev => ({ ...prev, [street.id]: { ...prev[street.id], [`label${suffix}`]: val.toString() } }))
                                }
                                error={errors[`label${suffix}`]}
                                required
                                type="text"
                            />
                            <Field
                                label={t(`${translationKey}.value`, lang)}
                                value={newFund.value}
                                onChange={(val) => setNewFund(prev => ({ ...prev, [street.id]: { ...prev[street.id], value: val.toString() } }))}
                                error={errors.value}
                                required
                                type="number"
                            />
                            <div className={generalClasses['field-block__actions']}>
                                <button onClick={saveNewFund}>{t(`actions.save`, lang)}</button>
                                <button onClick={cancelAddFund}>{t(`actions.cancel`, lang)}</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default StreetDocumentation;
